import React, { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Trophy, Calendar, MapPin, TrendingUp, Zap } from 'lucide-react';
import { TechnicalPick } from '../TechnicalPicksCard';

interface Team {
  name: string;
  logo?: string;
}

interface GameData {
  homeTeam: Team;
  awayTeam: Team;
  league?: string;
  leagueLogo?: string;
  date?: string;
  time?: string;
  venue?: string;
}

interface SG7ShareCardProps {
  gameData: GameData;
  confidenceScore?: number;
  pick?: TechnicalPick;
  format: 'story' | 'square';
  mode?: 'preview' | 'export';
  className?: string;
}

// Hardcoded colors for export mode (CSS variables don't compute in html-to-image)
const EXPORT_COLORS = {
  surface1: '#141414',
  surface2: '#1f1f1f',
  gold: '#D4A437',
  goldDim: '#8B7355',
  white: '#FFFFFF',
  muted: '#999999',
  border: '#383838',
  gradientGold: 'linear-gradient(135deg, #8B7355 0%, #D4A437 100%)',
};

// Get proxy URL for external images
const getProxiedUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  try {
    const parsedUrl = new URL(url);
    const externalHosts = ['media.api-sports.io', 'v3.football.api-sports.io'];
    
    if (externalHosts.includes(parsedUrl.hostname)) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (supabaseUrl) {
        return `${supabaseUrl}/functions/v1/image-proxy?url=${encodeURIComponent(url)}`;
      }
    }
  } catch {
    // Invalid URL, return as-is
  }
  
  return url;
};

// Get team initials for fallback
const getTeamInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 3)
    .toUpperCase();
};

// Team logo component with fallback
const TeamLogo = ({ 
  src, 
  name, 
  size = 160,
  isExport = false,
}: { 
  src?: string; 
  name: string; 
  size?: number;
  isExport?: boolean;
}) => {
  const [hasError, setHasError] = React.useState(false);
  const proxiedSrc = getProxiedUrl(src);
  
  const fallbackStyle = isExport ? {
    backgroundColor: EXPORT_COLORS.surface2,
    borderColor: EXPORT_COLORS.border,
  } : {};

  const initialsStyle = isExport ? {
    color: EXPORT_COLORS.muted,
    fontSize: size * 0.25,
  } : { fontSize: size * 0.25 };
  
  return (
    <div 
      style={{ width: size, height: size, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {/* Fallback initials */}
      <div 
        className={cn(
          "absolute inset-0 rounded-full flex items-center justify-center",
          !isExport && "bg-sg7-surface-2 border-2 border-sg7-border"
        )}
        style={{
          ...fallbackStyle,
          borderWidth: isExport ? 2 : undefined,
          borderStyle: isExport ? 'solid' : undefined,
          borderRadius: '50%',
          opacity: hasError || !proxiedSrc ? 1 : 0,
        }}
      >
        <span 
          className={cn(!isExport && "font-bold text-sg7-muted")}
          style={initialsStyle}
        >
          {getTeamInitials(name)}
        </span>
      </div>
      
      {/* Actual logo */}
      {proxiedSrc && !hasError && (
        <img 
          src={proxiedSrc} 
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', zIndex: 10 }}
          crossOrigin="anonymous"
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
};

// League logo component with fallback
const LeagueLogo = ({ src, size = 48 }: { src?: string; size?: number }) => {
  const [hasError, setHasError] = React.useState(false);
  const proxiedSrc = getProxiedUrl(src);
  
  if (!proxiedSrc || hasError) {
    return null;
  }
  
  return (
    <img 
      src={proxiedSrc} 
      alt="" 
      style={{ width: size, height: size, objectFit: 'contain' }}
      crossOrigin="anonymous"
      onError={() => setHasError(true)}
    />
  );
};

export const SG7ShareCard = forwardRef<HTMLDivElement, SG7ShareCardProps>(
  ({ gameData, confidenceScore, pick, format, mode = 'preview', className }, ref) => {
    const isStory = format === 'story';
    const isExport = mode === 'export';
    
    const dimensions = isStory 
      ? { width: 1080, height: 1920 } 
      : { width: 1080, height: 1080 };
    
    const scale = isExport ? 1 : (isStory ? 0.25 : 0.35);
    
    const confidenceLevel = confidenceScore 
      ? confidenceScore >= 75 ? 'high' : confidenceScore >= 50 ? 'medium' : 'low'
      : 'medium';

    // For export mode: use inline styles with hardcoded colors
    // For preview mode: use Tailwind classes
    const containerStyle: React.CSSProperties = isExport ? {
      width: dimensions.width,
      height: dimensions.height,
      background: `linear-gradient(135deg, ${EXPORT_COLORS.surface1} 0%, ${EXPORT_COLORS.surface2} 50%, ${EXPORT_COLORS.surface1} 100%)`,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    } : {
      width: dimensions.width,
      height: dimensions.height,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    };

    return (
      <div 
        ref={ref}
        className={cn(
          "sg7-share-card",
          !isExport && "relative overflow-hidden bg-gradient-to-br from-sg7-surface-1 via-sg7-surface-2 to-sg7-surface-1",
          className
        )}
        style={containerStyle}
      >
        {/* Background pattern */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.05,
            backgroundImage: `radial-gradient(circle at 20% 30%, ${EXPORT_COLORS.gold} 0%, transparent 50%),
                             radial-gradient(circle at 80% 70%, ${EXPORT_COLORS.gold} 0%, transparent 50%)`,
          }}
        />
        
        {/* Content container */}
        <div style={{ 
          position: 'relative', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          padding: 64 
        }}>
          
          {/* Header: SG7 Branding */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 48 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ 
                width: 64, 
                height: 64, 
                borderRadius: 12, 
                background: EXPORT_COLORS.gradientGold,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Zap style={{ width: 40, height: 40, color: EXPORT_COLORS.surface1 }} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <h1 style={{ 
                  fontSize: 48, 
                  fontWeight: 900, 
                  color: EXPORT_COLORS.white, 
                  letterSpacing: '-0.02em',
                  margin: 0,
                  lineHeight: 1,
                }}>SG7</h1>
                <p style={{ 
                  fontSize: 18, 
                  color: EXPORT_COLORS.gold, 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.15em',
                  margin: 0,
                }}>Premium Analysis</p>
              </div>
            </div>
          </div>

          {/* League info */}
          {gameData.league && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 40 }}>
              <LeagueLogo src={gameData.leagueLogo} size={48} />
              <span style={{ 
                fontSize: 24, 
                color: EXPORT_COLORS.muted, 
                textTransform: 'uppercase', 
                letterSpacing: '0.1em',
                fontWeight: 500,
              }}>
                {gameData.league}
              </span>
            </div>
          )}

          {/* Teams section */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, marginBottom: 40 }}>
              {/* Home team */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ marginBottom: 24 }}>
                  <TeamLogo 
                    src={gameData.homeTeam.logo} 
                    name={gameData.homeTeam.name}
                    size={160}
                    isExport={isExport}
                  />
                </div>
                <span style={{ 
                  fontSize: 30, 
                  fontWeight: 700, 
                  color: EXPORT_COLORS.white, 
                  maxWidth: 280,
                  lineHeight: 1.2,
                }}>
                  {gameData.homeTeam.name}
                </span>
              </div>

              {/* VS divider */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 32px' }}>
                <div style={{ 
                  width: 96, 
                  height: 96, 
                  borderRadius: '50%', 
                  border: `4px solid ${EXPORT_COLORS.gold}33`,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <span style={{ fontSize: 30, fontWeight: 900, color: EXPORT_COLORS.gold }}>VS</span>
                </div>
              </div>

              {/* Away team */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ marginBottom: 24 }}>
                  <TeamLogo 
                    src={gameData.awayTeam.logo} 
                    name={gameData.awayTeam.name}
                    size={160}
                    isExport={isExport}
                  />
                </div>
                <span style={{ 
                  fontSize: 30, 
                  fontWeight: 700, 
                  color: EXPORT_COLORS.white, 
                  maxWidth: 280,
                  lineHeight: 1.2,
                }}>
                  {gameData.awayTeam.name}
                </span>
              </div>
            </div>

            {/* Date and venue */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, color: EXPORT_COLORS.muted, fontSize: 20 }}>
              {gameData.date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar style={{ width: 24, height: 24 }} />
                  <span>{gameData.date}</span>
                  {gameData.time && <span>• {gameData.time}</span>}
                </div>
              )}
              {gameData.venue && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MapPin style={{ width: 24, height: 24 }} />
                  <span style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {gameData.venue}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Pick section (if available) */}
          {pick && (
            <div style={{ margin: '40px 0' }}>
              <div style={{ 
                backgroundColor: `${EXPORT_COLORS.surface2}CC`, 
                border: `2px solid ${EXPORT_COLORS.gold}4D`,
                borderRadius: 24, 
                padding: 40 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <Trophy style={{ width: 32, height: 32, color: EXPORT_COLORS.gold }} />
                  <span style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: EXPORT_COLORS.gold, 
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Pick Principal
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 40, fontWeight: 700, color: EXPORT_COLORS.white }}>
                    {pick.label}
                  </span>
                  {pick.odds && (
                    <span style={{ fontSize: 40, fontWeight: 900, color: EXPORT_COLORS.gold }}>
                      @{pick.odds.toFixed(2)}
                    </span>
                  )}
                </div>
                
                {pick.confidence && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 18, color: EXPORT_COLORS.muted, marginBottom: 8 }}>
                      <span>Confidence</span>
                      <span style={{ color: EXPORT_COLORS.gold, fontWeight: 700 }}>{pick.confidence}%</span>
                    </div>
                    <div style={{ height: 16, backgroundColor: EXPORT_COLORS.surface1, borderRadius: 8, overflow: 'hidden' }}>
                      <div 
                        style={{ 
                          height: '100%', 
                          background: EXPORT_COLORS.gradientGold,
                          borderRadius: 8,
                          width: `${pick.confidence}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confidence meter (if no pick but has confidence) */}
          {!pick && confidenceScore !== undefined && (
            <div style={{ margin: '40px 0' }}>
              <div style={{ 
                backgroundColor: `${EXPORT_COLORS.surface2}CC`, 
                border: `2px solid ${EXPORT_COLORS.gold}4D`,
                borderRadius: 24, 
                padding: 40 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <TrendingUp style={{ width: 32, height: 32, color: EXPORT_COLORS.gold }} />
                  <span style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: EXPORT_COLORS.gold, 
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Confidence Level
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 24, backgroundColor: EXPORT_COLORS.surface1, borderRadius: 12, overflow: 'hidden' }}>
                      <div 
                        style={{ 
                          height: '100%', 
                          borderRadius: 12,
                          width: `${confidenceScore}%`,
                          background: confidenceLevel === 'high' 
                            ? EXPORT_COLORS.gradientGold
                            : confidenceLevel === 'medium'
                              ? 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)'
                              : 'linear-gradient(90deg, #dc2626 0%, #ef4444 100%)',
                        }}
                      />
                    </div>
                  </div>
                  <span style={{ fontSize: 48, fontWeight: 900, color: EXPORT_COLORS.gold }}>
                    {confidenceScore}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ 
            marginTop: 'auto', 
            paddingTop: 40, 
            borderTop: `2px solid ${EXPORT_COLORS.border}` 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 24, color: EXPORT_COLORS.white, fontWeight: 500, marginBottom: 8, margin: 0 }}>
                  📲 View the full analysis
                </p>
                <p style={{ fontSize: 20, color: EXPORT_COLORS.gold, fontWeight: 700, margin: 0 }}>
                  studiogool7.com
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 16, color: EXPORT_COLORS.muted, margin: 0 }}>
                  Bet responsibly
                </p>
                <p style={{ fontSize: 16, color: EXPORT_COLORS.muted, margin: 0 }}>
                  18+ | Responsible gaming
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

SG7ShareCard.displayName = 'SG7ShareCard';
